// Which sign-ins actually issued a token -- the frontend twin of the runbook's
// Test-TokenIssuingSignIn (runbook 2026-10-13.1). Keep the two rules in step.
//
// "Success" is not enough. When Conditional Access refuses an AiTM attempt, the Management
// API still records the MFA steps (RequestType SAS:BeginAuth / SAS:EndAuth) as successful
// UserLoggedIn rows, with the phishing URL as the ApplicationId, under the SAME
// CorrelationId as the refused attempt. On IR-20261013-levig-001 all 22 "successful"
// phishing-kit rows were such steps; no token was ever issued (Silver handoff 2026-10-13).
//
// A refused attempt is keyed (CorrelationId, IP), never the bare CorrelationId: Microsoft
// reuses that id across users and days. The all-ff placeholder is never a key.

export const PLACEHOLDER_CORRELATION_ID = 'ff000000-0000-0000-0000-000000000000';
const GUID = /^[0-9a-f]{8}-([0-9a-f]{4}-){3}[0-9a-f]{12}$/i;

const corr = (e) => String(e?.CorrelationId || '').trim().toLowerCase();
const attemptKey = (e) => `${corr(e)}|${String(e?.IPAddress || '').toLowerCase()}`;

/** (CorrelationId, IP) keys of every attempt that failed or that Conditional Access refused. */
export function refusedAttemptKeys(signIns) {
  const keys = new Set();
  for (const e of signIns || []) {
    const c = corr(e);
    if (!c || c === PLACEHOLDER_CORRELATION_ID) continue;
    const failed = e.ResultStatus !== 'Success' || String(e.ConditionalAccessStatus || '').toLowerCase() === 'failure';
    if (failed) keys.add(attemptKey(e));
  }
  return keys;
}

/** True when this sign-in issued a token: Success, not an MFA step, a real app, attempt not refused. */
export function isTokenIssuingSignIn(e, refusedKeys) {
  if (!e || e.ResultStatus !== 'Success') return false;
  if (/^SAS:/i.test(String(e.RequestType || ''))) return false;
  const app = String(e.ApplicationId || '').trim();
  if (app && !GUID.test(app)) return false;
  const c = corr(e);
  if (c && c !== PLACEHOLDER_CORRELATION_ID && refusedKeys && refusedKeys.has(attemptKey(e))) return false;
  return true;
}

/**
 * For a window anchored on phishing-kit sign-ins: how many of those rows belong to a refused
 * attempt, and whether ANY sign-in from the kit's addresses issued a token.
 * `allRefused` is the claim the attacker-window panel makes ("every attempt was refused,
 * no token was issued"), so it requires both: every kit row refused, and no token-issuing
 * sign-in from any address the kit used.
 */
export function phishingKitRefusal(kitLegs, signIns) {
  const legs = kitLegs || [];
  const refused = refusedAttemptKeys(signIns);
  const refusedLegs = legs.filter((e) => {
    const c = corr(e);
    return c && c !== PLACEHOLDER_CORRELATION_ID && refused.has(attemptKey(e));
  }).length;
  const kitIPs = new Set(legs.map((e) => String(e.IPAddress || '').toLowerCase()).filter(Boolean));
  const tokenFromKitIP = (signIns || []).some(
    (e) => kitIPs.has(String(e.IPAddress || '').toLowerCase()) && isTokenIssuingSignIn(e, refused)
  );
  return {
    refusedLegs,
    tokenFromKitIP,
    allRefused: legs.length > 0 && refusedLegs === legs.length && !tokenFromKitIP,
  };
}
